import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { WoltModal, APIErrorNotification } from 'common/components';

import CreatableSelect from './CreatableSelect';

type Props = {
  options: { label: string; value: string }[];
  isLoading: boolean;
  error?: any;
  onConfirm: (areaOption: { value: string; label: string }) => void;
  onClose: () => void;
};

const Root = styled.form``;

const StyledModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  overflow: visible;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0 24px;
`;

const StyledSelect = styled(CreatableSelect)`
  width: 392px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const MoveItemsModal = ({ options, isLoading, error, onConfirm, onClose }: Props) => {
  const intl = useIntl();
  const [value, setValue] = useState<string | null>(null);

  const isConfirmButtonDisabled = isLoading || !value;

  const handleConfirm = useCallback(() => {
    if (!value) {
      return;
    }
    const existingOption = options.find(option => option.value === value);
    onConfirm(existingOption || { value, label: value });
  }, [onConfirm, options, value]);

  return (
    <Root
      onKeyPress={event => {
        if (event.key === 'Enter' && !isConfirmButtonDisabled) {
          handleConfirm();
        }
      }}
    >
      <StyledModal isOpen onRequestClose={onClose}>
        <ModalHeader onCloseClick={onClose}>
          <FormattedMessage id="picking-route.modal-move-title" />
        </ModalHeader>
        <StyledModalContent>
          <FormRow label={intl.formatMessage({ id: 'picking-route.picking-area' })} labelAlign="center">
            <StyledSelect
              options={options}
              value={value}
              onChange={setValue}
              disabled={isLoading}
              formatCreateLabel={inputValue =>
                intl.formatMessage({ id: 'picking-route.create-option' }, { label: inputValue })
              }
            />
          </FormRow>
        </StyledModalContent>
        <StyledModalFooter>
          <ActionsContainer>
            <Button variant="secondary" size="small" onClick={onClose} disabled={isLoading}>
              <FormattedMessage id="state.cancel" />
            </Button>
            <Button
              variant="primary"
              size="small"
              style={{ marginInlineStart: 18 }}
              onClick={handleConfirm}
              disabled={isConfirmButtonDisabled}
              loading={isLoading}
            >
              <FormattedMessage id="state.save" />
            </Button>
          </ActionsContainer>
          {error && <APIErrorNotification error={error} style={{ marginBlockStart: 18 }} />}
        </StyledModalFooter>
      </StyledModal>
    </Root>
  );
};

export default MoveItemsModal;
